/**
 * Column definitions for the affiliate Active and Candidates tables.
 * Mirrors legacy `modules/Affiliate/components/List/Active/columns.tsx` and
 * `List/Candidates/columns.tsx` — mapped to @vnetwork/ui primitives.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import { Badge, Button } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { formatDate, formatMoney } from '@/lib/format';
import type { Affiliate, AffiliateCandidate, AffiliateStatus, AffiliateTier } from '@/api/affiliate';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';

/* ── Status / tier → tone maps ──────────────────────────────────────────── */

const STATUS_TONE: Record<AffiliateStatus, 'warning' | 'success' | 'danger' | 'neutral'> = {
  pending: 'warning',
  active: 'success',
  suspended: 'danger',
  inactive: 'neutral',
};

const STATUS_LABEL: Record<AffiliateStatus, string> = {
  pending: 'Pending',
  active: 'Active',
  suspended: 'Suspended',
  inactive: 'Inactive',
};

const TIER_TONE: Record<AffiliateTier, 'neutral' | 'info' | 'warning' | 'success'> = {
  standard: 'neutral',
  silver: 'info',
  gold: 'warning',
  platinum: 'success',
};

const TIER_LABEL: Record<AffiliateTier, string> = {
  standard: 'Standard',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
};

/* ── Active affiliate columns ───────────────────────────────────────────── */

export const AFFILIATE_ACTIVE_COLUMNS: ColumnDef<Affiliate, unknown>[] = [
  {
    id: 'referrer_name',
    header: 'Affiliate Name',
    cell: ({ row }) => (
      <TableLink to={`/affiliates/${row.original.affiliate_id}`}>
        {row.original.referrer_name || '—'}
      </TableLink>
    ),
  },
  {
    id: 'referrer_email',
    header: 'Email',
    cell: ({ row }) => row.original.referrer_email || '—',
  },
  {
    id: 'referral_code',
    header: 'Referral Code',
    cell: ({ row }) => (
      <code style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.referral_code}
      </code>
    ),
  },
  {
    id: 'tier',
    header: 'Tier',
    cell: ({ row }) => {
      const tier = row.original.tier;
      return (
        <Badge tone={TIER_TONE[tier] ?? 'neutral'}>
          {TIER_LABEL[tier] ?? tier}
        </Badge>
      );
    },
  },
  {
    id: 'commission_percent',
    header: 'Commission %',
    cell: ({ row }) => `${row.original.commission_percent}%`,
  },
  {
    id: 'total_clicks',
    header: 'Clicks',
    cell: ({ row }) => row.original.total_clicks ?? 0,
  },
  {
    id: 'total_conversions',
    header: 'Conversions',
    cell: ({ row }) => row.original.total_conversions ?? 0,
  },
  {
    id: 'total_commission',
    header: 'Total Commission',
    cell: ({ row }) => formatMoney(row.original.total_commission ?? 0),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status;
      return (
        <StatusPill tone={STATUS_TONE[status] ?? 'neutral'}>
          {STATUS_LABEL[status] ?? status}
        </StatusPill>
      );
    },
  },
  {
    id: 'created_at',
    header: 'Created',
    cell: ({ row }) => formatDate(row.original.created_at),
  },
];

/* ── Candidate columns ──────────────────────────────────────────────────── */

interface CandidateColumnsOptions {
  navigate: (path: string) => void;
}

export function buildCandidateColumns(
  opts: CandidateColumnsOptions,
): ColumnDef<AffiliateCandidate, unknown>[] {
  return [
    {
      id: 'contact_name',
      header: 'Contact Name',
      cell: ({ row }) => {
        const { customer_id, customer_contact_id, contact_name } = row.original;
        return (
          <TableLink to={`/customers/${customer_id}?contact_id=${customer_contact_id}`}>
            {contact_name || '—'}
          </TableLink>
        );
      },
    },
    {
      id: 'customer_name',
      header: 'Customer',
      cell: ({ row }) => (
        <EntityLink
          to={`/customers/${row.original.customer_id}`}
          id={row.original.customer_id}
          name={row.original.customer_name}
        />
      ),
    },
    {
      id: 'contact_email',
      header: 'Email',
      cell: ({ row }) => row.original.contact_email || '—',
    },
    {
      id: 'contact_phone',
      header: 'Phone',
      cell: ({ row }) => row.original.contact_phone || '—',
    },
    {
      id: 'contact_roles',
      header: 'Roles',
      cell: ({ row }) => row.original.contact_roles?.join(', ') || '—',
    },
    {
      id: 'linked_status',
      header: 'Linked Status',
      cell: ({ row }) =>
        row.original.linked_entity_id ? (
          <StatusPill tone="success">Linked</StatusPill>
        ) : (
          <StatusPill tone="warning">Not linked</StatusPill>
        ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        if (row.original.linked_entity_id) {
          return (
            <TableLink to={`/affiliates/${row.original.linked_entity_id}`}>
              View affiliate
            </TableLink>
          );
        }
        const params = new URLSearchParams({
          customer_id: String(row.original.customer_id),
          referrer_name: row.original.contact_name || '',
          referrer_email: row.original.contact_email || '',
          referrer_phone: row.original.contact_phone || '',
          referrer_company: row.original.customer_name || '',
        });
        return (
          <Button
            size="sm"
            onClick={() => opts.navigate(`/affiliates/new?${params}`)}
          >
            Create affiliate
          </Button>
        );
      },
    },
  ];
}
